import { useTranslation } from "react-i18next";
import React, { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSortDown, faSortUp } from "@fortawesome/free-solid-svg-icons";

const FinancialReportTable = ({
  financialData,
  sortConfig = { key: null, direction: "asc" },
  onSort = () => {},
}) => {
  const { t } = useTranslation();

  const columns = useMemo(
    () => [
      { key: "massCode", label: t("financial_report_mass_code") },
      { key: "massName", label: t("financial_report_mass_name") },
      { key: "serviceName", label: t("financial_report_service_name") },
      { key: "planName", label: t("plan_name_label") },
      { key: "status", label: t("status_label") },
      { key: "invoiceNumber", label: t("financial_report_invoice_number") },
      { key: "invoiceDate", label: t("financial_report_invoice_date") },
      { key: "basicAmount", label: t("financial_report_basic_amount") },
      { key: "igstAmount", label: t("financial_report_igst_amount") },
      { key: "sgstAmount", label: t("financial_report_sgst_amount") },
      { key: "cgstAmount", label: t("financial_report_cgst_amount") },
      { key: "totalAmount", label: t("financial_report_total_amount") },
      { key: "gstNumberOfMass", label: t("financial_report_gst_number") },
      { key: "planDate", label: t("financial_report_plan_date") },
      { key: "minutesProvided", label: t("financial_report_minutes_provided") },
      { key: "messagesProvided", label: t("financial_report_messages_provided") },
      { key: "autoDebitRepurchase", label: "Auto Debit Repurchase" },
    ],
    [t]
  );

  const renderHeaderContent = (col) => {
    if (col.key !== "invoiceDate") return col.label;
    const isActive = sortConfig.key === col.key;
    const icon = !isActive
      ? faSortDown
      : sortConfig.direction === "asc"
        ? faSortUp
        : faSortDown;
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
        <span>{col.label}</span>
        <button
          type="button"
          onClick={() => onSort(col.key)}
          style={{
            border: "none",
            background: "transparent",
            padding: 0,
            cursor: "pointer",
            color: isActive ? "#1f2937" : "#666",
          }}
          aria-label={`Sort ${col.label}`}
        >
          <FontAwesomeIcon icon={icon} />
        </button>
      </span>
    );
  };

  const hasManyColumns = columns.length > 10;

  return (
    <div
      className={`services-table-wrapper financial-report-wrapper ${
        hasManyColumns ? "scrollable" : ""
      }`}
    >
      <table
        className={`services-table financial-report-table ${
          hasManyColumns ? "has-many-columns" : ""
        }`}
      >
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key}>{renderHeaderContent(col)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {financialData && financialData.length > 0 ? (
            financialData.map((item, index) => (
              <TableRow key={index} item={item} columns={columns} />
            ))
          ) : (
            <tr>
              <td colSpan={columns.length} className="px-6 py-4 text-center">
                {t("no_data_available")}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const TableRow = ({ item, columns }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? "" : d.toLocaleDateString();
  };

  const formatAmount = (val) => {
    if (val == null || val === "") return "-";
    const num = parseFloat(val);
    return isNaN(num) ? val : num.toFixed(2);
  };

  const renderCell = (key) => {
    switch (key) {
      case "invoiceDate":
      case "planDate":
        return formatDate(item[key]);
      case "basicAmount":
      case "igstAmount":
      case "sgstAmount":
      case "cgstAmount":
      case "totalAmount":
        return formatAmount(item[key]);
      default:
        return item[key] ?? "-";
    }
  };

  return (
    <tr>
      {columns.map((col) => (
        <td key={col.key} data-label={col.label}>
          {renderCell(col.key)}
        </td>
      ))}
    </tr>
  );
};

export default FinancialReportTable;